/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';

// Component imports
import { Button, Checkbox, Col, Form, Input, Modal, Popover, Radio, Row, Select, Space, Tabs, Tooltip, Typography } from 'antd';
import { ShareAltOutlined, SettingOutlined, CheckCircleOutlined, CloseOutlined, CopyOutlined, MinusOutlined, PlayCircleOutlined, QuestionCircleOutlined, ReloadOutlined, SyncOutlined, WarningOutlined } from '@ant-design/icons';
import EntryPointSelect from '../Forms/Fields/EntryPointSelect';
import BotAvatar from './BotAvatar';
import { ExternalLinkIcon } from '../Icons/ExternalLinkIcon';
import CustomDrawer from './CustomDrawer';

// Misc imports
import { API_NLP_SERVICE_URL, API_WEBCHAT_URL, IS_HRWIZ_CONSOLE_ENABLED } from '../../constants/env';
import { LANGUAGE_OPTIONS, NLP_PIPELINE_STATUSES, WEBCHAT_WIDGET_MODES } from '../../constants/main';
import { NAV_BOT, NAV_BOT_PREVIEW } from '../../constants/navigation';
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import { resetScrollPosition } from '../../utils/dom/resetScrollPosition';
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useLazyFetchBotQuery, useTrainBotMutation } from '../../store/bots/botsApi';
import { useLazyFetchAutomatedAnswersQuery } from '../../store/automatedAnswers/automatedAnswersApi';
import { useLazyFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';
import generateWebchatScript from '../../utils/misc/generateWebchatScript';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { PLUGIN_CATEGORY } from '../../constants/integrations';

// Constant declarations
const baseClassName = 'bot-preview';
const { TabPane } = Tabs;
const { TextArea } = Input;

const PREVIEW_MODAL_TABS = {
  PREVIEW: 'preview',
  SETTINGS: 'settings'
};

const UntrainedBotTooltip = ({ hidden, children }) => {
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();

  return (
    <Tooltip title={
      !hidden ? (
        <Space direction="vertical">
          {
            <Trans t={t}>
              Agent <Typography.Text strong type="secondary">training has failed</Typography.Text>, so it has not been updated with the latest changes in Automated Answers. While you can preview the Agent\'s behavior here, please note that the most recent changes made will not be included or taken into consideration in its behavior.
            </Trans>
          }

          {
            IS_HRWIZ_CONSOLE_ENABLED ? (
              <Typography.Text strong>
                {t('Contact support if this issue does not resolve.')}
              </Typography.Text>
            ) : (
              <Link
                to={replaceUrlParams(NAV_BOT, { tenantId, orgId }) + `?view=artificial-intelligence&tab=nlp-systems`}
                className="fw-bold"
              >
                {t('Check NLP Systems')}
              </Link>
            )
          }
        </Space>
      ) : null
    }>
      {children}
    </Tooltip>
  );
};

const BotTestButtons = ({
  disabled,
  showText = true,
  className = '',
  showPreviewButton = true,
  showTrainButton = true
}) => {
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const shareableLinkRef = useRef();
  const previewRef = useRef();
  const chatScriptRef = useRef(null);

  const { currentUser } = useSelector((state) => state.auth);
  const { botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const [dragDisabled, setDragDisabled] = useState(true);
  const [activeTab, setActiveTab] = useState(PREVIEW_MODAL_TABS.PREVIEW);
  const [previewBounds, setPreviewBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const [previewPageUrl, setPreviewPageUrl] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [currentPreviewEntryPoint, setCurrentPreviewEntryPoint] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(false);
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const [isPreviewInNewTabPopoverOpen, setIsPreviewInNewTabPopoverOpen] = useState(false);
  const { data: previewDeployment, isFetching: isLoadingPreviewDeployment } = getCurrentQueryState('fetchBotDeploymentPreview', { orgId, tenantId });
  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [fetchBot] = useLazyFetchBotQuery();
  const [fetchNlpPipelines] = useLazyFetchNlpPipelinesQuery({});
  const [trainBot, { isLoading: isTrainingBot }] = useTrainBotMutation();
  const [fetchAutomatedAnswers, { data: automatedAnswers }] = useLazyFetchAutomatedAnswersQuery();
  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  const botDivId = useMemo(() => bot?.settings?.webchat?.configuration?.customSettings?.botDivId ?? 'botDiv', [bot]);
  const hasAutomatedAnswers = !!DEFAULT_FALLBACK_ID && (automatedAnswers?.items || []).filter(a => a.id !== DEFAULT_FALLBACK_ID)?.length > 0;
  const intervalIdRef = useRef();

  const botTrainingStatus = useMemo(() => {
    if (!bot) {
      return null;
    }

    const nlpMap = bot.nlpMap || {};

    // If all are ready, then botTrainingStatus = NLP_PIPELINE_STATUSES.READY
    // Same if there are no automated answers
    if (Object.values(nlpMap).every(pipeline => pipeline?.status === NLP_PIPELINE_STATUSES.READY) || !hasAutomatedAnswers) {
      return NLP_PIPELINE_STATUSES.READY;
    }

    // If at least one is failed, then botTrainingStatus = NLP_PIPELINE_STATUSES.FAILED
    if (Object.values(nlpMap).some(pipeline => pipeline?.status === NLP_PIPELINE_STATUSES.FAILED)) {
      return NLP_PIPELINE_STATUSES.FAILED;
    }

    // In all other cases, botTrainingStatus = NLP_PIPELINE_STATUSES.OUTDATED
    return NLP_PIPELINE_STATUSES.OUTDATED;
  }, [bot, automatedAnswers?.items]);

  // On mount
  useEffect(() => {
    // Related to bot preview
    // Mount the webchat script
    const script = document.createElement('script');
    script.src = API_WEBCHAT_URL;
    document.body.appendChild(script);

    // Related to bot training
    if (intervalIdRef.current && !isBotTraining(bot)) {
      stopTrainingInterval();
    }

    if (!intervalIdRef.current && isBotTraining(bot)) {
      setIsPolling(true);
      fetchBot({ orgId, tenantId });
      startTrainingInterval();
    }

    return () => {
      if (intervalIdRef.current) {
        stopTrainingInterval();
      }

      setIsSettingsCollapsed(false);

      if (chatScriptRef.current) {
        chatScriptRef.current.remove();
        chatScriptRef.current = null;
      }
    };
  }, []);

  //#region Bot Preview
  // When the modal is opened or closed
  useEffect(() => {
    setActiveTab(PREVIEW_MODAL_TABS.PREVIEW);

    if (isPreviewModalOpen) {
      setIsMinimized(false);
      if (!chatScriptRef.current) {
        loadWebchatScript();
      }
    } else {
      form.resetFields();
      if (chatScriptRef.current) {
        chatScriptRef.current.remove();
        chatScriptRef.current = null;
      }
    }
  }, [isPreviewModalOpen]);

  // When the URL search params change, since we keep track of the "previewEntryPoint" param
  useEffect(() => {
    const previewEntryPoint = searchParams.get('previewEntryPoint');

    if (previewEntryPoint) {
      setCurrentPreviewEntryPoint(previewEntryPoint);
      handlePreviewButtonVisibility();

      // Set the entry point and restart
      setEntryPoint(searchParams.get('previewEntryPoint'));
      searchParams.delete('previewEntryPoint');
      setSearchParams(searchParams);
      if (currentBotLanguage !== form.getFieldValue('language')) {
        form.setFieldValue('language', currentBotLanguage);
        restartWebchat();
      }
      if (currentPreviewEntryPoint !== previewEntryPoint) {
        restartWebchat();
      }
    }
  }, [searchParams.get('previewEntryPoint'), currentBotLanguage]);

  // Recalculate the preview URL when any bot content is changed
  useEffect(() => {
    onPreviewURLChange();
  }, [bot, form.getFieldValue('entryNode'), form.getFieldValue('deploymentId'), form.getFieldValue('language')]);

  // Reload the modal and fetch the new chapter on tenant change
  useEffect(() => {
    if (window.HBFWebchat?.remove && chatScriptRef.current && document.getElementById(botDivId)) {
      window.HBFWebchat.remove();
      if (chatScriptRef.current) {
        chatScriptRef.current.remove();
        chatScriptRef.current = null;
      }
      setActiveTab(PREVIEW_MODAL_TABS.PREVIEW);
    }

    if (form) {
      form.resetFields();
    }

    return () => {
      setIsSettingsCollapsed(false);
      setIsPreviewModalOpen(false);
    };
  }, [tenantId]);

  // When we are editing a single activity, switch to this one if the modal is not already open
  useEffect(() => {
    if (!isPreviewModalOpen) {
      setEntryPoint(activity?.content?.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id);
    }
  }, [activity?.content]);

  useEffect(() => {
    if (isPreviewModalOpen) {
      if (isMinimized) {
        setIsSettingsCollapsed(false);
        resetScrollPosition('.ant-modal-content');

        // Allow scrolling and interacting with the rest of the app when minimized
        document.body.classList.remove('ant-scrolling-effect');
        document.body.style.overflow = 'visible';

        // Pausing any videos and audios playing in case window is minimized
        const videos = document.querySelectorAll('video');
        const audios = document.querySelectorAll('audio');
        videos.forEach(video => {
          if (!video.paused) {
            video.pause();
          }
        });
        audios.forEach(audio => {
          if (!audio.paused) {
            audio.pause();
          }
        });
      } else {
        // Disable scrolling and interacting with the rest of the app when not minimized
        document.body.classList.add('ant-scrolling-effect');
        document.body.style.overflow = 'hidden';
      }
    }
  }, [isMinimized]);

  const setEntryPoint = (entryPoint) => {
    if (form) {
      form.setFieldsValue({ entryNode: entryPoint || '' });
    }
  };

  const setPreviewLanguage = (language => {
    if (form) {
      form.setFieldsValue({ language: language || primaryBotLanguage });
    }
  });

  const restartWebchat = async (navigateToWebchatTab = true) => {
    if (window.HBFWebchat && window.HBFWebchat.remove && typeof window.HBFWebchat.remove === 'function' && chatScriptRef.current) {
      // Remove the webchat (close the directline socket)
      await window.HBFWebchat.remove();

      // Remove webchat script and reload it
      if (chatScriptRef.current) {
        chatScriptRef.current.remove();
        chatScriptRef.current = null;
      }
      loadWebchatScript();

      if (navigateToWebchatTab) {
        setActiveTab(PREVIEW_MODAL_TABS.PREVIEW);
      }
    }
  };

  const onPreviewURLChange = (currentMode = null, shouldOnlyReturnURL = false) => {
    let validMetadata = form.getFieldValue('metadata');
    if (validMetadata) {
      try {
        JSON.parse(validMetadata);
      } catch {
        validMetadata = '';
      }
    }

    if (bot) {
      const entryPointParam = form.getFieldValue('entryNode') ? `&greeting=${form.getFieldValue('entryNode')}` : '';
      const previewUrl = replaceUrlParams(NAV_BOT_PREVIEW, { tenantId })
      +`?deploymentId=${previewDeployment?.id}`
      +`&name=${encodeURIComponent(bot.displayName)}`
      +`&description=${encodeURIComponent(bot.description)}`
      +`&analytics=${form.getFieldValue('analytics')}`
      +`&metadata=${validMetadata?.replace(/\s+/g, '').replace(/\n/g, '')}` // Remove all whitespaces and line breaks
      +`&language=${form.getFieldValue('language')}`
      +`&mode=${currentMode ? currentMode : form.getFieldValue('mode')}`
      + entryPointParam;

      if(shouldOnlyReturnURL) {
        return previewUrl;
      }
      setPreviewPageUrl(previewUrl);

      return previewUrl;
    } else {
      if(shouldOnlyReturnURL) {
        return null;
      }

      setPreviewPageUrl(null);
      return null;
    }
  };

  const onPreviewMove = (_event, uiData) => {
    const { clientWidth, clientHeight } = window && window.document ? window.document.documentElement : {};
    const targetRect = previewRef?.current?.getBoundingClientRect();

    setPreviewBounds(
      {
        left: -targetRect?.left + uiData?.x,
        right: clientWidth - (targetRect?.right - uiData?.x),
        top: -targetRect?.top + uiData?.y,
        bottom: clientHeight - (targetRect?.bottom - uiData?.y)
      }
    );
  };

  const loadWebchatScript = () => {
    if(!previewDeployment?.id) {
      return;
    }
    const scriptToAdd = generateWebchatScript({
      deploymentId: previewDeployment?.id,
      introNode: form.getFieldValue('entryNode'),
      isAnalyticsEnabled: form.getFieldValue('analytics'),
      metadata: form.getFieldValue('metadata'),
      userName: currentUser?.fullName,
      userEmail: currentUser?.email,
      userId: currentUser?.id,
      language: form.getFieldValue('language') ? form.getFieldValue('language') : primaryBotLanguage,
      mode: WEBCHAT_WIDGET_MODES.EMBEDDED
    });

    const script = document.createElement('script');
    const scriptFunc = document.createTextNode(scriptToAdd);
    script.appendChild(scriptFunc);
    //scriptRef.current = script;

    setTimeout(() => {
      const parent = document?.getElementById(botDivId)?.parentElement;

      if (parent && !chatScriptRef?.current) {
        parent.appendChild(script);
        chatScriptRef.current = script;
      }
    });
  };

  const handlePreviewButtonVisibility = () => {
    if (!isPreviewModalOpen) {
      setIsPreviewModalOpen(true);
    } else {
      setIsMinimized(false);
    }
  };
  //#endregion

  //#region Bot Training
  useEffect(() => {
    if (orgId && tenantId) {
      // Set true to preferCacheValue
      fetchAutomatedAnswers({ orgId, tenantId }, true);
    }
  }, [orgId, tenantId]);

  const isBotTraining = (bot) => {
    return Object.entries(bot?.nlpMap || {}).some(([, pipeline]) => pipeline?.status === NLP_PIPELINE_STATUSES.TRAINING);
  };

  const startTrainingBot = async () => {
    const hasTrainingStarted = await trainBot({
      orgId,
      tenantId,
      ...bot?.systemSettings?.nlpService === 'HBF_NLP' && { apiUrl: API_NLP_SERVICE_URL }
    }).unwrap();

    if (hasTrainingStarted) {
      if (searchParams.get('tab') === 'nlp-systems') {
        fetchNlpPipelines({ orgId, tenantId });
      }
      setIsPolling(true);
      await fetchBot({ orgId, tenantId });
      await startTrainingInterval();
    }
  };

  const startTrainingInterval = useCallback(async () => {
    intervalIdRef.current = setInterval(async () => {
      const fetchedBot = await fetchBot({ orgId, tenantId }).unwrap();
      const hasBotFinishedTraining = !isBotTraining(fetchedBot);

      if(hasBotFinishedTraining){
        stopTrainingInterval();
      } else {
        await fetchBot({ orgId, tenantId });
      }
    }, [5000]);

  }, [orgId, tenantId]);

  const stopTrainingInterval = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'nlp-systems') {
      fetchNlpPipelines({ orgId, tenantId });
    }
    clearInterval(intervalIdRef.current);
    intervalIdRef.current = null;
    setIsPolling(false);
  };
  //#endregion

  const renderButtons = (shouldShowTrainButton = showTrainButton, shouldShowPreviewButton = showPreviewButton, shouldShowText = showText) => {
    const renderTrainIcon = () => {
      if (botTrainingStatus === NLP_PIPELINE_STATUSES.TRAINING || isTrainingBot || isFetchingBot || isPolling) {
        return <SyncOutlined spin />;
      }

      if (botTrainingStatus === NLP_PIPELINE_STATUSES.READY) {
        return <CheckCircleOutlined />;
      }

      if (botTrainingStatus === NLP_PIPELINE_STATUSES.FAILED) {
        return <WarningOutlined />;
      }

      // Else
      return <SyncOutlined />;
    };

    const renderTrainText = () => {
      if (!shouldShowText) {
        return null;
      }

      if (botTrainingStatus === NLP_PIPELINE_STATUSES.TRAINING || isTrainingBot || isPolling) {
        return t('Training...');
      }

      if (botTrainingStatus === NLP_PIPELINE_STATUSES.READY) {
        return IS_HRWIZ_CONSOLE_ENABLED ? t('Agent Ready') : t('Agent Trained');
      }

      if (botTrainingStatus === NLP_PIPELINE_STATUSES.FAILED) {
        return t('Training Failed');
      }

      if (isFetchingBot) {
        return t('Fetching Status...');
      }

      // Else
      return t('Train Agent');
    };

    return bot && botTrainingStatus ? (
      <Space
        className={classNames(`${baseClassName}-buttons-wrapper`, `${baseClassName}-buttons`, {
          [`${baseClassName}-buttons-trained`]: botTrainingStatus === NLP_PIPELINE_STATUSES.READY,
          [`${baseClassName}-buttons-failed`]: botTrainingStatus === NLP_PIPELINE_STATUSES.FAILED,
          [`${baseClassName}-buttons-untrained`]: botTrainingStatus === NLP_PIPELINE_STATUSES.OUTDATED
        }, className)}
      >
        {
          shouldShowTrainButton && (
            (bot?.nlpMap && bot?.nlpMap[primaryBotLanguage] && hasAutomatedAnswers)
            || (bot?.systemSettings?.isAgent && bot?.plugins?.find(plugin => plugin.category === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE)?.settings?.enabled)
          ) ? (
              <UntrainedBotTooltip
                hidden={botTrainingStatus !== NLP_PIPELINE_STATUSES.FAILED}
              >
                <Button
                  type="text"
                  className={classNames(`${baseClassName}-button-train`, {
                    'pointer-events-none': disabled || botTrainingStatus === NLP_PIPELINE_STATUSES.READY || isTrainingBot || isFetchingBot || isPolling
                  })}
                  onClick={startTrainingBot}
                  icon={renderTrainIcon()}
                >
                  {renderTrainText()}
                </Button>
              </UntrainedBotTooltip>
            ) : null
        }

        {
          shouldShowPreviewButton ? (
            <Button
              type="icon"
              disabled={!previewDeployment?.id}
              className={classNames(`${baseClassName}-button-preview`, {
                'pointer-events-none': disabled || isLoadingPreviewDeployment || !previewDeployment?.id
              })}
              onClick={() => {
                handlePreviewButtonVisibility();
                setPreviewLanguage(currentBotLanguage);
                restartWebchat();
              }}
            >
              <PlayCircleOutlined />
            </Button>
          ) : null
        }
      </Space>
    ) : null;
  };

  const renderModalTitle = () => {
    return (
      <Row
        gutter={8}
        justify="space-between"
        className={isMinimized ? 'cursor-pointer' : 'cursor-move'}
        onMouseOver={() => dragDisabled ? setDragDisabled(false) : null}
        onMouseOut={() => setDragDisabled(true)}
        wrap={false}
        align="middle"
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <Col flex="auto">
          <BotAvatar bot={bot} avatarSize={28} showBotName={true} />
        </Col>

        <Col flex="none">
          <Space hidden={isMinimized}>
            {renderButtons(true, false, true)}
            {
              botTrainingStatus === NLP_PIPELINE_STATUSES.OUTDATED ? (
                <UntrainedBotTooltip>
                  <QuestionCircleOutlined className="fs-sm" />
                </UntrainedBotTooltip>
              ) : null
            }
          </Space>
          <Button
            type="text"
            icon={<MinusOutlined />}
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-secondary mx-1"
          />

          <Button type="text"
            icon={<CloseOutlined />}
            onClick={() => {
              // Hack for fixing the .ant-scrolling-effect margin on the right
              // We could have called setIsPreviewModalOpen(false) directly without touching isMinimized but a margin is left in the screen
              setIsMinimized(false);
              setIsSettingsCollapsed(false);
              setTimeout(setIsPreviewModalOpen(false));

              // On Modal close reset the body behavior
              document.body.classList.remove('ant-scrolling-effect');
              document.body.style.overflow = 'visible';
            }}
            className="text-secondary mx-1"
          />
        </Col>
      </Row>
    );
  };

  return (
    <Fragment>
      {renderButtons()}

      <Form
        form={form}
        name="previewSettings"
        onValuesChange={() => {
          onPreviewURLChange();
        }}
        initialValues={{
          analytics: false,
          metadata: '',
          mode: WEBCHAT_WIDGET_MODES.EMBEDDED,
          entryNode: searchParams.get('previewEntryPoint') || (activity && activity.content?.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id)
        }}
        labelCol={{
          span: 8
        }}
        wrapperCol={{
          span: 12
        }}
        layout="horizontal"
        labelAlign="left"
        labelWrap
        colon={false}
      >
        <Modal
          title={renderModalTitle()}
          open={isPreviewModalOpen}
          transitionName={isPreviewModalOpen ? 'ant-zoom' : ''} // Hack for only showing animation when opening the modal - to avoid glitches when minimized
          width="fit-content"
          wrapClassName={classNames(`${baseClassName}-modal`, {
            [`${baseClassName}-modal__minimized`]: isMinimized
          })}
          centered
          className="scrollbar-custom scrollbar-custom-nohide"
          mask={false}
          keyboard={false}
          closable={false}
          onCancel={() => setIsMinimized(true)}
          destroyOnClose
          footer={null}
          modalRender={modal => (
            <Draggable
              disabled={dragDisabled || isMinimized}
              bounds={previewBounds}
              onStart={(event, uiData) => onPreviewMove(event, uiData)}
            >
              <div className="w-fc" ref={previewRef}>{modal}</div>
            </Draggable>
          )}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            hidden={isMinimized}
            className="mt-2"
            tabBarExtraContent={
              <Fragment>
                <Row
                  justify="space-between"
                  wrap={false}
                  align="middle"
                  className="px-3"
                >
                  <Col>
                    <Space size={0} direction="vertical" className="fs-sm">
                      <div style={{ width: 204 }}>
                        <Form.Item
                          name="entryNode"
                          className="mb-0 mt-2"
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                        >
                          <EntryPointSelect
                            onClear={() => {
                              setCurrentPreviewEntryPoint(null);
                            }}
                            onChange={() => restartWebchat(false)}
                            className="w-100"
                            popupClassName="w-30"
                          />
                        </Form.Item>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="text"
                        className="border-0"
                        icon={<ReloadOutlined/>}
                        title="Restart"
                        style={{ maxWidth: 100 }}
                        disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                        onClick={() => {
                          window.HBFWebchat?.restart();
                        }}
                      />
                      <Button
                        type="text"
                        className={classNames('border-0', {
                          'text-primary bg-primary-light': isSettingsCollapsed
                        })}
                        icon={<SettingOutlined />}
                        title="Settings"
                        onClick={() => {
                          setIsSettingsCollapsed(!isSettingsCollapsed);
                        }}
                        style={{ maxWidth: 100 }}
                        disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                      />
                      <Popover
                        title={
                          <Typography.Text className="fs-base text-muted">
                            {t('Copy link to clipboard')}
                          </Typography.Text>
                        }
                        placement="bottomRight"
                        content={
                          <Row className="d-flex flex-column">
                            <Col xs={24}>
                              <Form.Item
                                name="mode"
                                label={t('Webchat mode (for Tab preview)')}
                                colon
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                className="mt-0"
                                tooltip={{
                                  icon: <QuestionCircleOutlined />,
                                  title: t('Available only for browser tab preview. Embedded: among the webpage\'s content Bubble: initial state is on the right bottom of the webpage as a bubble')
                                }}
                              >
                                <Radio.Group>
                                  <Radio key={WEBCHAT_WIDGET_MODES.EMBEDDED} value={WEBCHAT_WIDGET_MODES.EMBEDDED}>
                                    {t(WEBCHAT_WIDGET_MODES.EMBEDDED, { context: 'webchat widget modes' })}
                                  </Radio>
                                  <Radio key={WEBCHAT_WIDGET_MODES.BUBBLE} value={WEBCHAT_WIDGET_MODES.BUBBLE}>
                                    {t(WEBCHAT_WIDGET_MODES.BUBBLE, { context: 'webchat widget modes' })}
                                  </Radio>
                                </Radio.Group>
                              </Form.Item>
                            </Col>
                            <Col xs={24}>
                              <span className="d-flex">

                                <Input
                                  ref={shareableLinkRef}
                                  className="mb-0 w-100 mr-1 align-self-center"
                                  value={window.location.origin + previewPageUrl}
                                  readOnly={true}
                                />

                                <Button
                                  type="primary"
                                  icon={<CopyOutlined/>}
                                  onClick={() => {
                                    shareableLinkRef.current.select();
                                    copyTextToClipboard(window.location.origin + previewPageUrl, t('Agent link copied!'));
                                  }}
                                />
                              </span>
                            </Col>
                          </Row>
                        }
                        trigger="click"
                        onOpenChange={(open) => {
                          setIsSettingsCollapsed(false);
                          setIsSharePopoverOpen(open);
                        }}
                        overlayClassName={!previewPageUrl ? 'd-none' : ''}
                      >
                        <Button
                          title="Share"
                          type="text"
                          className={classNames('border-0', {
                            'text-primary bg-primary-light': isSharePopoverOpen
                          })}
                          icon={<ShareAltOutlined/>}
                          style={{ maxWidth: 100 }}
                          disabled={!previewPageUrl || !window.HBFWebchat || !window.HBFWebchat.remove}
                        />
                      </Popover>
                      <Popover
                        title={
                          <Typography.Text className="fs-base text-muted">
                            {t('Preview in a new tab')}
                          </Typography.Text>
                        }
                        placement="bottomRight"
                        trigger="click"
                        overlayClassName={!previewPageUrl ? 'd-none' : ''}
                        onOpenChange={(open) => {
                          setIsSettingsCollapsed(false);
                          setIsPreviewInNewTabPopoverOpen(open);
                        }}
                        content={
                          <Row className="d-flex flex-column">
                            <Col xs={24}>
                              <Space direction="vertical">
                                <Button
                                  type="text"
                                  className="border-0 fw-400"
                                  onClick={() => {
                                    const previewUrl = onPreviewURLChange(WEBCHAT_WIDGET_MODES.EMBEDDED, true);
                                    window.open(previewUrl, '_blank', 'noopener,noreferrer');
                                  }}
                                  disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                                >
                                  {t('Embedded Mode')}
                                </Button>
                                <Button
                                  type="text"
                                  className="border-0 fw-400"
                                  onClick={() => {
                                    const previewUrl = onPreviewURLChange(WEBCHAT_WIDGET_MODES.BUBBLE, true);
                                    window.open(previewUrl, '_blank', 'noopener,noreferrer');
                                  }}
                                  disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                                >
                                  {t('Bubble Mode')}
                                </Button>
                              </Space>
                            </Col>
                          </Row>
                        }
                      >
                        <Button
                          title="Open in new tab"
                          type="text"
                          className={classNames('border-0', {
                            'text-primary bg-primary-light': isPreviewInNewTabPopoverOpen
                          })}
                          icon={<ExternalLinkIcon />}
                          style={{ maxWidth: 100 }}
                          disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                        />
                      </Popover>
                    </Space>
                  </Col>
                </Row>
                <Row
                  wrap={false}
                  align="middle"
                  className="w-100 mt-2"
                >
                  <Col xs={24} className="p-0">
                    <CustomDrawer
                      isVisible={isSettingsCollapsed}
                      elementStyles={{ zIndex: 99999 }} // Need to avoid being cover by CSAT popup of webchat
                    >
                      <Row>
                        <Col xs={24}>
                          <Row
                            justify="space-between"
                            wrap={false}
                            align="middle"
                          >
                            <Col>
                              <Typography.Text className="fs-lg">
                                <SettingOutlined className="pr-4" /> {t('Preview Settings')}
                              </Typography.Text>
                            </Col>
                            <Col>
                              <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => setIsSettingsCollapsed(false)}
                              />
                            </Col>

                          </Row>
                        </Col>
                        <Col xs={24}>
                          <Space
                            direction="vertical"
                            className="w-100 pl-10"
                          >
                            <Form.Item
                              name="analytics"
                              label={t('Enable analytics for this preview')}
                              valuePropName="checked"
                              tooltip={{
                                icon: <QuestionCircleOutlined />,
                                title: t('Mark as checked in case you wish to record your conversation through this preview in Chat Sessions')
                              }}
                            >
                              <Checkbox className="ml-2"/>
                            </Form.Item>

                            <Form.Item
                              name="language"
                              label={t('Preview Language')}
                              tooltip={{
                                icon: <QuestionCircleOutlined />,
                                title: t('Select the language you wish to preview your current webchat flow. Defaults to agent\'s primary language.')
                              }}
                            >
                              <Select
                                className="ml-2"
                                placeholder={t('Select a language')}
                                defaultValue={currentBotLanguage}
                              >
                                {
                                  botLanguages.map((language) => {
                                    return (
                                      <Select.Option key={language} value={language}>
                                        {LANGUAGE_OPTIONS.find(lang => lang.key === language).label}
                                      </Select.Option>
                                    );
                                  })
                                }
                              </Select>
                            </Form.Item>

                            <Form.Item
                              name="metadata"
                              label={t('Pass metadata as JSON')}
                              rules={[{
                                validator: async (rule, value) => {
                                  try {
                                    if (value) {
                                      JSON.parse(value);
                                    }
                                  } catch {
                                    throw new Error(rule.message);
                                  }
                                },
                                message: t('The input isn\'t valid JSON')
                              }]}
                            >
                              <TextArea
                                autoSize={{ minRows: 2 }}
                                className="text-code resize-none ml-2"
                                spellCheck={false}
                              />
                            </Form.Item>

                            <Row>
                              <Col offset={8} xs={12}>
                                <Button
                                  className="w-100 ml-2"
                                  type="primary"
                                  disabled={!window.HBFWebchat || !window.HBFWebchat.remove}
                                  onClick={() => {
                                    restartWebchat();
                                    setIsSettingsCollapsed(!isSettingsCollapsed);
                                  }}
                                >
                                  {t('Save & Restart')}
                                </Button>
                              </Col>
                            </Row>
                          </Space>
                        </Col>
                      </Row>
                    </CustomDrawer>
                  </Col>
                </Row>
              </Fragment>

            }
          >
            <TabPane
              tab={null}
              key={PREVIEW_MODAL_TABS.PREVIEW}
              forceRender
            >
              <div id={botDivId}></div>
            </TabPane>
          </Tabs>
        </Modal>
      </Form>
    </Fragment>
  );
};

BotTestButtons.propTypes = {
  disabled: PropTypes.bool,
  showText: PropTypes.bool,
  className: PropTypes.string,
  showPreviewButton: PropTypes.bool,
  showTrainButton: PropTypes.bool
};

UntrainedBotTooltip.propTypes = {
  children: PropTypes.node,
  hidden: PropTypes.bool
};

export default BotTestButtons;
